import React from 'react';
import { Icon } from '@fluentui/react';
import CompetitionControl from './CompetitionControl';
import PipedriveIcon from './PipedriveIcon';
import { formatArr, formatInteger, formatMoney, metricText, metricTitle, sortByMetric } from '../utils/metrics';
import { hasPolandCoordinate } from '../utils/geo';
import {
  competitionSummaryForItems,
  facilityCompetitionProvider,
  providerColor,
  providerTextColor,
  sortByCompetition,
} from '../utils/competition';
import { activityColor, activityDateText, activityFreshness, activitySummaryForItems, activityValueText, sortByActivity } from '../utils/activity';

function uniqueById(items = []) {
  return Array.from(new Map(items.map((item) => [String(item.id), item])).values());
}

function PipedriveMark({ linked }) {
  if (!linked) return null;
  return (
    <span className="prospecting-pipedrive-mark" title="Powiązane z Pipedrive" aria-label="Powiązane z Pipedrive">
      <PipedriveIcon size={17} />
    </span>
  );
}

function CompetitionValue({ item, scope }) {
  const provider = scope === 'commune'
    ? facilityCompetitionProvider(item)
    : item?.competition_summary?.dominant_provider;
  const count = scope === 'commune' ? null : Number(item?.competition_summary?.known_units_count || 0);
  if (!provider) return <span className="prospecting-side-value is-competition is-empty">brak danych</span>;
  return (
    <span className="prospecting-side-value is-competition">
      <i style={{ backgroundColor: providerColor(provider) }} aria-hidden="true" />
      <span>{provider}{count ? ` · ${count}` : ''}</span>
    </span>
  );
}

function ActivityValue({ item, scope }) {
  const at = scope === 'commune' ? item?.last_activity_at : item?.activity_summary?.latest_activity_at;
  if (activityFreshness(at) <= 0) return <span className="prospecting-side-value is-activity is-empty">brak ≤ 12 mies.</span>;
  const count = scope === 'commune' ? null : Number(item?.activity_summary?.active_count || 0);
  return (
    <span className="prospecting-side-value is-activity">
      <i style={{ backgroundColor: activityColor(at) }} aria-hidden="true" />
      <span>{activityDateText(at)}{count ? ` · ${count}` : ''}</span>
    </span>
  );
}

function AreaList({ items, metric, product, overlay = 'metric', hoveredId, onHover, onSelect }) {
  const sorted = overlay === 'competition'
    ? sortByCompetition(uniqueById(items), 'area')
    : overlay === 'activity'
      ? sortByActivity(uniqueById(items), 'area')
      : sortByMetric(uniqueById(items), metric);
  const jstProduct = product === 'grants' || product === 'erecruitment';
  return (
    <div className="prospecting-side-items">
      {sorted.map((item) => (
        <button type="button" key={item.id} className={`prospecting-side-row ${hoveredId === String(item.id) ? 'is-hovered' : ''}`} onMouseEnter={() => onHover(String(item.id))} onMouseLeave={() => onHover(null)} onClick={() => onSelect(item)}>
          <span className="prospecting-side-row-main">
            <strong>{item.name}</strong>
            <span>{jstProduct
              ? `${formatInteger(item.scopes_count)} JST · ${formatInteger(item.targets_count)} placówek`
              : `${formatInteger(item.targets_count)} celów${Number(item.own_units_count || 0) ? ` · ${formatInteger(item.own_units_count)} naszych` : ''}${item.arr_complete ? '' : ' · ARR niepełny'}`}</span>
          </span>
          {overlay === 'competition'
            ? <CompetitionValue item={item} scope="area" />
            : overlay === 'activity'
              ? <ActivityValue item={item} scope="area" />
              : <span className="prospecting-side-value">{metricText(item, metric)}</span>}
          <Icon iconName="ChevronRight" />
        </button>
      ))}
    </div>
  );
}

function FacilityGroups({ items, groups, metric, overlay = 'metric', hoveredId, onHover, onSelect, onSyncGroup }) {
  const uniqueItems = uniqueById(items);
  const byId = new Map(uniqueItems.map((item) => [String(item.id), item]));
  return (
    <div className="prospecting-facility-groups">
      {groups.map((group) => (
        <section key={group.name} className="prospecting-facility-group">
          <div className="prospecting-facility-group-head">
            <div className="prospecting-facility-group-title-row">
              <strong>{group.name}</strong>
              {onSyncGroup ? (
                <button
                  type="button"
                  className="prospecting-crm-group-add"
                  onClick={() => onSyncGroup(group)}
                  title={`Dodaj / synchronizuj grupę ${group.name} do Pipedrive`}
                  aria-label={`Dodaj / synchronizuj grupę ${group.name} do Pipedrive`}
                >+</button>
              ) : null}
            </div>
            <span className="prospecting-facility-group-meta">{group.count} placówek · {formatInteger(group.targets_count || 0)} celów{Number(group.own_count || 0) ? ` · ${formatInteger(group.own_count)} naszych` : ''} · {metricText(group, 'children')} · {metricText(group, 'mrr')} MRR · {formatArr(group)}</span>
          </div>
          <div className="prospecting-side-items">
            {(overlay === 'competition'
              ? sortByCompetition(group.items.map((id) => byId.get(String(id))).filter(Boolean), 'commune')
              : overlay === 'activity'
                ? sortByActivity(group.items.map((id) => byId.get(String(id))).filter(Boolean), 'commune')
                : sortByMetric(group.items.map((id) => byId.get(String(id))).filter(Boolean), metric)).map((item) => {
              const operator = String(item.operator_name || '').trim();
              const linked = Boolean(item.crm_linked);
              const provider = overlay === 'competition' ? facilityCompetitionProvider(item) : null;
              return (
                <button type="button" key={item.id} className={`prospecting-side-row is-facility ${item.is_own_customer ? 'is-own-customer' : ''} ${hoveredId === String(item.id) ? 'is-hovered' : ''}`} onMouseEnter={() => onHover(String(item.id))} onMouseLeave={() => onHover(null)} onClick={() => onSelect(item)}>
                  <span
                    className="prospecting-side-map-index"
                    aria-hidden="true"
                    style={overlay === 'competition' && provider ? { backgroundColor: providerColor(provider), color: providerTextColor(provider) } : overlay === 'activity' ? { backgroundColor: activityColor(item.last_activity_at), color: activityFreshness(item.last_activity_at) > 0.52 ? '#fff' : '#173b5d' } : undefined}
                  >{item.map_index || '·'}</span>
                  <span className="prospecting-side-row-main">
                    <strong>{item.name}{item.is_own_customer ? <span className="prospecting-own-badge">NASZA</span> : null}</strong>
                    <span className="prospecting-side-subtitle">
                      <PipedriveMark linked={linked} />
                      <span>{operator || 'Brak danych o prowadzącym'}{!hasPolandCoordinate(item.longitude, item.latitude) ? ' · brak współrzędnych' : ''}</span>
                    </span>
                  </span>
                  {overlay === 'competition'
                    ? <CompetitionValue item={item} scope="commune" />
                    : overlay === 'activity'
                      ? <ActivityValue item={item} scope="commune" />
                      : <span className="prospecting-side-value">{metricText(item, metric)}</span>}
                  <Icon iconName="ChevronRight" />
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function JstSummary({ token, product, summary, onSyncJst, onChanged }) {
  if (!summary) return <div className="prospecting-jst-empty">Brak jednostek w zakresie produktu dla tej JST.</div>;
  const categories = summary.categories || [];
  return (
    <div className="prospecting-jst-summary">
      <div className="prospecting-jst-crm-head">
        <div>
          <PipedriveMark linked={Boolean(summary.jst_crm_linked)} />
          <span>{summary.jst_name || 'JST'}{summary.jst_registry_number ? ` · ${summary.jst_registry_number}` : ''}</span>
        </div>
        {onSyncJst ? <button type="button" className="prospecting-crm-group-btn" onClick={onSyncJst}>Dodaj / synchronizuj JST</button> : null}
      </div>
      {summary.jst_entity_id ? <CompetitionControl token={token} entityId={summary.jst_entity_id} product={product} onChanged={onChanged} /> : null}
      <div className="prospecting-jst-category-list">
        {categories.map((category) => (
          <div key={category.key} className="prospecting-jst-category-row">
            <span>
              <strong>{category.label}</strong>
              {product === 'grants' && Number(category.unit_rate || 0) > 0 ? <small>{formatMoney(category.unit_rate)} / placówkę / rok</small> : product === 'erecruitment' ? <small>{formatInteger(category.points || 0)} punktów rekrutacyjnych</small> : null}
            </span>
            <span className="prospecting-jst-category-values"><strong>{formatInteger(category.count || 0)}</strong>{product === 'grants' ? <small>{formatMoney(category.arr || 0)}</small> : null}</span>
          </div>
        ))}
      </div>
      {product === 'grants' ? (
        <>
          <div className="prospecting-jst-detail-row"><span>Opłata podstawowa JST</span><strong>{formatMoney(summary.base_fee || 0)}</strong></div>
          <div className="prospecting-jst-total-row"><span>Potencjalny ARR</span><strong>{formatMoney(summary.potential_arr || 0)}</strong></div>
        </>
      ) : (
        <>
          <div className="prospecting-jst-detail-row"><span>Punkty rekrutacyjne</span><strong>{formatInteger(summary.pricing_points_count || 0)}</strong></div>
          <div className="prospecting-jst-detail-row"><span>Procesy</span><strong>{formatInteger(summary.processes_count || 0)}</strong></div>
          <div className="prospecting-jst-total-row"><span>Potencjalny ARR 2027</span><strong>{formatMoney(summary.potential_arr || 0)}</strong></div>
        </>
      )}
      {summary.pricing_note ? <p className="prospecting-jst-pricing-note">{summary.pricing_note}</p> : null}
    </div>
  );
}

export default function MarketSideList({
  token, data, metric, overlay = 'metric', hoveredId, onHover, onSelectArea, onSelectEntity, onSyncGroup, onSyncJst, onCrmChanged,
}) {
  const scope = data?.scope?.level;
  const totals = data?.totals || {};
  const jstProduct = data?.product === 'grants' || data?.product === 'erecruitment';
  const competitionMode = overlay === 'competition' && data?.product === 'kidplace';
  const activityMode = overlay === 'activity' && data?.product === 'kidplace';
  const competitionSummary = competitionMode ? competitionSummaryForItems(data?.items || [], scope) : null;
  const activitySummary = activityMode ? activitySummaryForItems(data?.items || [], scope) : null;
  const title = scope === 'country' ? 'Województwa' : scope === 'voivodeship' ? 'Powiaty' : scope === 'county' ? 'Gminy' : jstProduct ? 'Zakres JST' : 'Placówki w gminie';
  return (
    <aside className={`prospecting-side-panel ${competitionMode ? 'is-competition' : ''} ${activityMode ? 'is-activity' : ''}`}>
      <div className="prospecting-side-summary">
        {competitionMode ? (
          <>
            <span>Kidplace · Konkurencja</span>
            <strong>{formatInteger(competitionSummary?.known_units_count || 0)} znanych wdrożeń</strong>
            <small>{formatInteger(competitionSummary?.own_units_count || 0)} Kidplace · {formatInteger(competitionSummary?.competitor_units_count || 0)} konkurencji · tylko widoczne typy placówek</small>
          </>
        ) : activityMode ? (
          <>
            <span>Kidplace · Aktywność handlowa</span>
            <strong>{formatInteger(activitySummary?.active_count || 0)} placówek z kontaktem</strong>
            <small>{activitySummary?.latest_activity_at ? `Najświeższa aktywność ${activityDateText(activitySummary.latest_activity_at)}` : 'Brak aktywności z ostatnich 12 miesięcy'} · tylko widoczne typy placówek</small>
          </>
        ) : (
          <>
            <span>{data?.product_label || 'Produkt'} · {metricTitle(metric)}</span>
            <strong>{metricText(totals, metric)}</strong>
            <small>{jstProduct
              ? `${formatInteger(totals.scopes_count || 0)} JST · ${formatInteger(totals.targets_count || 0)} placówek`
              : `${formatInteger(totals.targets_count)} celów${Number(totals.own_units_count || 0) ? ` · ${formatInteger(totals.own_units_count)} naszych` : ''} · ${formatInteger(totals.children_total || 0)} dzieci · ${formatMoney(totals.potential_mrr || 0)} MRR`}</small>
          </>
        )}
      </div>
      <div className="prospecting-side-title"><h2>{title}</h2><span>{scope === 'commune' && jstProduct ? formatInteger(totals.targets_count || 0) : uniqueById(data?.items || []).length}</span></div>
      {scope === 'commune' ? (
        jstProduct
          ? <JstSummary token={token} product={data.product} summary={data.jst_summary} onSyncJst={onSyncJst} onChanged={onCrmChanged} />
          : <FacilityGroups items={data.items || []} groups={data.groups || []} metric={metric} overlay={overlay} hoveredId={hoveredId} onHover={onHover} onSelect={onSelectEntity} onSyncGroup={onSyncGroup} />
      ) : <AreaList items={data?.items || []} metric={metric} overlay={overlay} product={data?.product} hoveredId={hoveredId} onHover={onHover} onSelect={onSelectArea} />}
    </aside>
  );
}
